import {useState} from "react";
import type { SchoolClass } from "./ClassesView";
import {
  attemptMemberIds,
  type LibraryCommand,
  type LibraryState,
  type SessionParticipationLevel,
  type StudentAttempt,
} from "./activity-library";

export function ParticipationHistory({
  attempt,
  state,
  classes,
  busy,
  readOnly,
  execute,
}: {
  attempt: StudentAttempt;
  state: LibraryState;
  classes: SchoolClass[];
  busy: boolean;
  readOnly: boolean;
  execute: (command: LibraryCommand) => Promise<unknown>;
}) {
  const [bulkScope, setBulkScope] = useState<Record<string, boolean>>({});
  const members = attemptMemberIds(attempt);
  const sessions = (state.digitalSessions ?? [])
    .filter(
      (session) =>
        session.classId === attempt.classId &&
        session.activityId === attempt.activityId &&
        (session.attemptIds?.includes(attempt.id) ||
          (!session.attemptIds?.length &&
            members.some((id) =>
              Object.hasOwn(session.participation ?? {}, id),
            ))),
    )
    .sort(
      (left, right) =>
        Date.parse(left.startedAt) - Date.parse(right.startedAt),
    );

  if (!sessions.length) return null;

  const name = (id: string) => {
    const student = classes
      .find((schoolClass) => schoolClass.id === attempt.classId)
      ?.students.find((row) => row.id === id);
    return student ? `${student.firstName} ${student.lastName}` : id;
  };
  const label = (level: SessionParticipationLevel) =>
    level === "ABS" ? "Absent" : level;

  return (
    <aside className="correction-participation-history">
      <h2>Participation active par séance</h2>
      <p>
        Chaque reprise de l’activité conserve sa propre participation.
        Modifier une séance ne remplace pas les autres.
      </p>
      {sessions.map((session, index) => (
        <section key={session.id}>
          <h3>
            Séance {index + 1} ·{" "}
            {new Date(session.startedAt).toLocaleDateString("fr-FR")} ·{" "}
            {new Date(session.startedAt).toLocaleTimeString("fr-FR", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </h3>
          {members.map((id) => {
            const value = session.participation?.[id]?.level ?? "B";
            const rowKey = `${session.id}:${id}`;
            const allPresent = !!bulkScope[rowKey];
            const isAbsent = value === "ABS";
            return (
              <div className="participation-row" key={id}>
                <span>{name(id)}</span>
                <span className="participation-scope" role="radiogroup" aria-label={`Portée de la participation — ${name(id)}`}>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!allPresent}
                    disabled={busy || readOnly}
                    onClick={() => setBulkScope((current) => ({...current, [rowKey]: false}))}
                  >
                    Cet élève
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={allPresent}
                    disabled={busy || readOnly || isAbsent}
                    title={isAbsent ? "Un élève absent ne peut pas servir de point de départ à une attribution collective." : undefined}
                    onClick={() => setBulkScope((current) => ({...current, [rowKey]: true}))}
                  >
                    Tous les présents
                  </button>
                </span>
                <select
                  aria-label={`Participation séance ${index + 1} — ${name(id)}`}
                  value={value}
                  disabled={busy || readOnly}
                  onChange={(event) => {
                    const level = event.target.value as SessionParticipationLevel;
                    if (!allPresent) {
                      void execute({
                        type: "session_participation",
                        sessionId: session.id,
                        studentId: id,
                        level,
                        expectedRevision: session.revision,
                      }).catch(() => {});
                      return;
                    }
                    if (level === "ABS") return;
                    if (!window.confirm(`Attribuer la participation ${label(level)} à tous les présents de cette séance ? Les participations déjà saisies seront remplacées.`)) return;
                    void execute({
                      type: "session_participation_present",
                      sessionId: session.id,
                      level,
                      expectedRevision: session.revision,
                    }).catch(() => {});
                  }}
                >
                  {(["A", "B", "C", "D", "ABS"] as SessionParticipationLevel[]).map(
                    (level) => (
                      <option key={level} value={level} disabled={allPresent && level === "ABS"}>
                        {label(level)}
                      </option>
                    ),
                  )}
                </select>
              </div>
            );
          })}
        </section>
      ))}
    </aside>
  );
}
